import { ReactNode } from 'react';
import type { EstadoPainelEquiplex } from '@/hooks/usePainelEquiplex';
import type { ChavePainel } from '@/config/origemDados';
import type { EquiplexSnapshotResponse, ExecucaoMeta } from '@/types/dashboards';
import { fmtData, fmtDataHora } from '@/utils/painelFormat';
import OrigemDadosCard from './OrigemDadosCard';

interface Props<P> {
  estado: EstadoPainelEquiplex<P>;
  titulo: string;
  subtitulo: string;
  /** Prefixo dos `data-testid` (`sop-atualizar`, `qualidade-frescor`…). */
  prefixo: string;
  corte: (payload: P) => string;
  /** Qual painel é, para o card "De onde vêm estes dados". */
  origem?: ChavePainel;
  children: (snapshot: { id: string; payload: P }) => ReactNode;
}

function BotaoAtualizar<P>({ estado, prefixo }: { estado: EstadoPainelEquiplex<P>; prefixo: string }) {
  const resposta = estado.resposta as EquiplexSnapshotResponse<P>;
  return (
    <button
      type="button"
      onClick={estado.atualizarAgora}
      disabled={estado.atualizando || estado.disparando || !resposta.replica_configurada}
      data-testid={`${prefixo}-atualizar`}
      title={resposta.replica_configurada ? 'Atualiza todas as abas do painel' : 'Réplica do Datasul não configurada neste ambiente'}
      className="print-hide inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-contrast hover:bg-accent-hover disabled:opacity-50"
    >
      <span aria-hidden className={`material-symbols-outlined text-[18px] ${estado.atualizando ? 'animate-spin' : ''}`}>
        sync
      </span>
      {estado.atualizando ? 'Atualizando…' : 'Atualizar agora'}
    </button>
  );
}

function AvisoFalha({ ultima, temSnapshot, prefixo }: { ultima: ExecucaoMeta; temSnapshot: boolean; prefixo: string }) {
  return (
    <div
      role="alert"
      data-testid={`${prefixo}-falha`}
      className="rounded-xl border border-theme-danger/40 bg-theme-danger/10 p-4 text-sm text-content-primary"
    >
      A última atualização ({fmtDataHora(ultima.created_at)}) falhou
      {temSnapshot ? ' — o painel mostra o último snapshot bom.' : '.'}
      {ultima.error && <div className="mt-1 font-mono text-xs text-content-secondary">{ultima.error}</div>}
    </div>
  );
}

function textoVazio<P>(resposta: EquiplexSnapshotResponse<P>): string {
  if (!resposta.pode_atualizar) return 'A primeira carga roda às 06:00. Volte mais tarde ou peça a um admin para atualizar.';
  if (!resposta.replica_configurada) return 'A réplica do Datasul não está configurada neste ambiente (AGENT_MYSQL_*).';
  return 'Clique em "Atualizar agora" para ler a réplica do Datasul. Depois disso, ele se atualiza todo dia às 06:00.';
}

type PropsCabecalho<P> = Pick<Props<P>, 'estado' | 'titulo' | 'subtitulo' | 'prefixo' | 'corte'>;

/**
 * O fim dos dados do snapshot: `frescor.dados_ate`, sempre um dia inteiro, até 23:59 (pedido da
 * Karla, 16/10/2026). Snapshot de antes disso não tem: vale o corte da aba, sem hora.
 */
function dadosAte(payload: unknown): string | null {
  const frescor = (payload as { frescor?: { dados_ate?: string | null } } | null)?.frescor;
  return frescor?.dados_ate ?? null;
}

interface PropsFrescor<P> {
  snapshot: ExecucaoMeta & { payload: P };
  prefixo: string;
  corte: (payload: P) => string;
}

/** "Dados até 15/10/2026 às 23:59 · atualizado em 16/10/2026 06:02" e, embaixo, a regra em uma linha. */
function Frescor<P>({ snapshot, prefixo, corte }: PropsFrescor<P>) {
  const ate = dadosAte(snapshot.payload);
  const atualizado = fmtDataHora(snapshot.finished_at ?? snapshot.created_at);
  return (
    <div className="text-right text-xs text-content-muted" data-testid={`${prefixo}-frescor`}>
      <p
        title={
          ate
            ? `Tudo o que o Datasul registrou até 23:59 de ${fmtData(ate)}. A cópia do Datasul roda de madrugada e o painel é atualizado às 06:00.`
            : undefined
        }
      >
        Dados até <strong className="text-content-secondary">{ate ? `${fmtData(ate)} às 23:59` : fmtData(corte(snapshot.payload))}</strong>
        {' · '}atualizado em {atualizado}
      </p>
      {ate && <p data-testid={`${prefixo}-frescor-regra`}>O que acontece hoje aparece amanhã, a partir das 06:00.</p>}
    </div>
  );
}

/** Título, "Atualizar agora" (ADMIN) e "dados até" do último snapshot. */
function Cabecalho<P>({ estado, titulo, subtitulo, prefixo, corte }: PropsCabecalho<P>) {
  const resposta = estado.resposta as EquiplexSnapshotResponse<P>;
  const { snapshot } = resposta;
  return (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <p className="text-xs uppercase tracking-wide text-content-muted">Painéis · Equiplex</p>
        <h1 className="text-2xl font-bold text-content-primary">{titulo}</h1>
        <p className="mt-1 text-sm text-content-secondary">{subtitulo}</p>
      </div>
      <div className="flex flex-col items-end gap-2">
        {resposta.pode_atualizar && <BotaoAtualizar estado={estado} prefixo={prefixo} />}
        {snapshot && <Frescor snapshot={snapshot} prefixo={prefixo} corte={corte} />}
      </div>
    </header>
  );
}

/**
 * Moldura comum das abas: título, botão "Atualizar agora" (ADMIN), "dados até",
 * de onde vêm os dados, aviso de falha e estado vazio. O conteúdo da aba recebe o snapshot pronto.
 */
function PainelShell<P>({ estado, titulo, subtitulo, prefixo, corte, origem, children }: Props<P>) {
  const { resposta, carregando, erro, atualizando, carregar } = estado;

  if (carregando) {
    return <div className="p-6 lg:p-10 text-content-muted">Carregando o painel…</div>;
  }

  if (!resposta) {
    return (
      <div className="p-6 lg:p-10">
        <div className="rounded-xl border border-theme-danger/40 bg-theme-danger/10 p-4 text-content-primary" role="alert">
          {erro}{' '}
          <button type="button" onClick={carregar} className="underline">
            Tentar de novo
          </button>
        </div>
      </div>
    );
  }

  const { snapshot, ultima_execucao: ultima } = resposta;

  return (
    <div className="report-printable p-6 lg:p-10 flex flex-col gap-6">
      <Cabecalho estado={estado} titulo={titulo} subtitulo={subtitulo} prefixo={prefixo} corte={corte} />

      {origem && <OrigemDadosCard chave={origem} />}

      {/* `ultima_execucao` só é FAILED quando nada deu certo depois da falha. */}
      {ultima?.status === 'FAILED' && !atualizando && (
        <AvisoFalha ultima={ultima} temSnapshot={!!snapshot} prefixo={prefixo} />
      )}

      {snapshot ? (
        children({ id: snapshot.id, payload: snapshot.payload })
      ) : (
        <div className="rounded-xl border border-theme-border bg-surface-card p-8 text-center" data-testid={`${prefixo}-vazio`}>
          <p className="font-semibold text-content-primary">
            {atualizando ? 'Gerando o primeiro snapshot…' : 'O painel ainda não foi gerado.'}
          </p>
          <p className="mt-1 text-sm text-content-muted">{textoVazio(resposta)}</p>
        </div>
      )}
    </div>
  );
}

export default PainelShell;
